import React, { useEffect, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { Container, Row, Col, Card, Badge, Alert, Spinner } from 'react-bootstrap';
import { getReservaById } from '../../services/firestoreService';
import { getClienteById } from '../../services/userService';
import { Reserva, Cliente } from '../../types';
import { formatPrice } from '../../utils/priceUtils';
import { formatNumeroReserva } from '../../utils/reservaUtils';
import { Calendar, User, Package, Hash, Clock, CheckCircle } from 'lucide-react';
import './PublicReservaView.css';

const PublicReservaView: React.FC = () => {
  const { id: routeId } = useParams<{ id: string }>();
  const location = useLocation();
  const fallbackId = location.pathname.match(/\/reserva\/([^/?#]+)/)?.[1];
  const id = routeId || fallbackId;
  const [reserva, setReserva] = useState<Reserva | null>(null);
  const [cliente, setCliente] = useState<Cliente | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');

  useEffect(() => {
    loadReservaData();
  }, [id]);

  const loadReservaData = async () => {
    if (!id) {
      setError('ID da reserva não fornecido');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const reservaResult = await getReservaById(id);

      if (reservaResult.success && reservaResult.data) {
        setReserva(reservaResult.data);

        // Carregar dados do cliente se disponível
        if (reservaResult.data.clienteId) {
          const clienteResult = await getClienteById(reservaResult.data.clienteId);
          if (clienteResult.success && clienteResult.data) {
            setCliente(clienteResult.data);
          }
        }
      } else {
        setError('Reserva não encontrada');
      }
    } catch (error) {
      setError('Erro ao carregar dados da reserva');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const getStatusBadge = (status: string) => {
    const statusConfig: { [key: string]: { variant: string; text: string; icon: React.ReactNode } } = {
      'reservado': {
        variant: 'warning',
        text: 'Reservado',
        icon: <Clock size={16} className="me-1" />
      },
      'retirado': {
        variant: 'info',
        text: 'Retirado',
        icon: <Package size={16} className="me-1" />
      },
      'devolvido': {
        variant: 'success',
        text: 'Devolvido',
        icon: <CheckCircle size={16} className="me-1" />
      },
      'cancelado': {
        variant: 'danger',
        text: 'Cancelado',
        icon: <Clock size={16} className="me-1" />
      }
    };

    const config = statusConfig[status] || {
      variant: 'secondary',
      text: status,
      icon: <Clock size={16} className="me-1" />
    };

    return (
      <Badge bg={config.variant} className="d-flex align-items-center">
        {config.icon}
        {config.text}
      </Badge>
    );
  };

  const getStatusPagamentoBadge = (status?: string) => {
    if (!status) return null;

    const statusConfig: { [key: string]: { variant: string; text: string } } = {
      'pago': { variant: 'success', text: 'Pago' },
      'pendente': { variant: 'warning', text: 'Pendente' },
      'parcial': { variant: 'info', text: 'Parcial' }
    };

    const config = statusConfig[status] || { variant: 'secondary', text: status };
    return <Badge bg={config.variant}>{config.text}</Badge>;
  };

  if (loading) {
    return (
      <Container className="py-5">
        <div className="text-center">
          <Spinner animation="border" variant="primary" />
          <p className="mt-3 text-muted">Carregando dados da reserva...</p>
        </div>
      </Container>
    );
  }

  if (error || !reserva) {
    return (
      <Container className="py-5">
        <Row className="justify-content-center">
          <Col md={8}>
            <Alert variant="danger" className="text-center">
              <h4>Reserva não encontrada</h4>
              <p className="mb-0">{error || 'A reserva solicitada não existe ou foi removida.'}</p>
            </Alert>
          </Col>
        </Row>
      </Container>
    );
  }

  return (
    <Container className="py-5">
      <Row className="justify-content-center">
        <Col lg={10}>
          {/* Header */}
          <Card className="border-0 shadow-lg mb-4" style={{ borderRadius: '20px' }}>
            <Card.Header
              className="text-white text-center py-4"
              style={{
                background: '#2a5298FF',
                borderRadius: '20px 20px 0 0'
              }}
            >
              <div className="d-flex align-items-center justify-content-center gap-3 mb-3">
                <Calendar size={40} />
                <div>
                  <h2 className="mb-0 fw-bold">Detalhes da Reserva</h2>
                  <p className="mb-0 opacity-90">SystemLoc - Sistema de Gestão</p>
                </div>
              </div>
            </Card.Header>

            <Card.Body className="p-4">
              {/* Informações da Reserva */}
              <Row className="mb-4">
                <Col md={6}>
                  <div className="d-flex align-items-center gap-3 mb-3">
                    <div className="bg-primary bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center" style={{ width: '50px', height: '50px' }}>
                      <Hash size={24} className="text-primary" />
                    </div>
                    <div>
                      <h5 className="mb-1 fw-bold text-dark">Número da Reserva</h5>
                      <span className="fw-semibold text-primary" style={{ fontFamily: 'monospace', fontSize: '18px' }}>
                        {formatNumeroReserva(reserva.numeroReserva || '')}
                      </span>
                    </div>
                  </div>
                </Col>
                <Col md={6}>
                  <div className="d-flex align-items-center gap-3 mb-3">
                    <div className="bg-success bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center" style={{ width: '50px', height: '50px' }}>
                      <Calendar size={24} className="text-success" />
                    </div>
                    <div>
                      <h5 className="mb-1 fw-bold text-dark">Status</h5>
                      {getStatusBadge(reserva.status)}
                    </div>
                  </div>
                </Col>
              </Row>

              {/* Datas */}
              <Row className="mb-4">
                <Col md={6}>
                  <Card className="h-100 border-0 shadow-sm" style={{ background: '#F1F3F5' }}>
                    <Card.Body className="text-center">
                      <Calendar size={32} className="mb-2" />
                      <h6 className="fw-bold">Data de Retirada</h6>
                      <p className="mb-0 fs-5 fw-semibold">{formatDate(reserva.dataRetirada)}</p>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={6}>
                  <Card className="h-100 border-0 shadow-sm" style={{ background: '#F1F3F5' }}>
                    <Card.Body className="text-center">
                      <Clock size={32} className="mb-2" />
                      <h6 className="fw-bold">Data de Devolução</h6>
                      <p className="mb-0 fs-5 fw-semibold">{formatDate(reserva.dataDevolucao)}</p>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>

              {/* Informações do Cliente */}
              <Card className="mb-4 border-0 shadow-sm">
                <Card.Header className=" text-dark">
                  <div className="d-flex align-items-center gap-2">
                    <User size={24} />
                    <h6 className="mb-0">Informações do Cliente</h6>
                  </div>
                </Card.Header>
                <Card.Body>
                  <Row>
                    <Col md={6}>
                      <div className="mb-3">
                        <label className="text-muted small mb-1">Nome Completo</label>
                        <p className="mb-0 fw-semibold text-dark">{reserva.clienteNome || cliente?.nome || 'Não informado'}</p>
                      </div>
                      {cliente?.telefone && (
                        <div className="mb-3">
                          <label className="text-muted small mb-1">Telefone</label>
                          <p className="mb-0 fw-semibold text-dark">{cliente.telefone}</p>
                        </div>
                      )}
                    </Col>
                    <Col md={6}>
                      {cliente?.cpf && (
                        <div className="mb-3">
                          <label className="text-muted small mb-1">CPF</label>
                          <p className="mb-0 fw-semibold text-dark">{cliente.cpf}</p>
                        </div>
                      )}
                      {cliente?.medidas && (
                        <div className="mb-3">
                          <label className="text-muted small mb-1">Medidas</label>
                          <p className="mb-0 fw-semibold text-dark">{cliente.medidas}</p>
                        </div>
                      )}
                    </Col>
                  </Row>
                  {cliente?.observacoes && (
                    <div className="mt-2 p-3 bg-light rounded">
                      <label className="text-muted small mb-1">Observações do Cliente</label>
                      <p className="mb-0 text-dark">{cliente.observacoes}</p>
                    </div>
                  )}
                </Card.Body>
              </Card>

              {/* Produtos */}
              {reserva.produtos && reserva.produtos.length > 0 && (
                <Card className="mb-4 border-0 shadow-sm">
                  <Card.Header className=" text-dark">
                    <div className="d-flex align-items-center gap-2">
                      <Package size={24} />
                      <h6 className="mb-0">Produtos Reservados</h6>
                    </div>
                  </Card.Header>
                  <Card.Body>
                    {reserva.produtos.map((produto, index) => (
                      <div key={index} className="border rounded p-3 mb-3" style={{ background: '#f8f9fa' }}>
                        <div className="d-flex align-items-center gap-2 mb-2">
                          {produto.produtoCodigoProduto && (
                            <span className="fw-bold" style={{ fontFamily: 'monospace', fontSize: '14px' }}>
                              {produto.produtoCodigoProduto}
                            </span>
                          )}
                          <h6 className="mb-0 fw-bold text-dark">{produto.produtoTitulo}</h6>
                        </div>
                        <Row>
                          <Col md={6}>
                            <p className="mb-1 text-muted"><strong>Tamanho:</strong> {produto.produtoTamanho}</p>
                            <p className="mb-1 text-muted"><strong>Cor:</strong> {produto.produtoCor}</p>
                          </Col>
                          <Col md={6}>
                            <p className="mb-1 text-muted"><strong>Quantidade:</strong> {produto.quantidade}</p>
                            {produto.precoUnitario && (
                              <p className="mb-1 text-success"><strong>Preço Unitário:</strong> {formatPrice(produto.precoUnitario)}</p>
                            )}
                          </Col>
                        </Row>
                        {produto.produtoObservacoes && (
                          <div className="mt-2 p-2 bg-warning bg-opacity-25 border border-warning rounded">
                            <strong className="text-warning-emphasis">Observações do produto:</strong>
                            <p className="mb-0 text-dark mt-1">{produto.produtoObservacoes}</p>
                          </div>
                        )}
                        {produto.precoTotal && (
                          <div className="text-end mt-2">
                            <Badge bg="primary" className="px-3 py-2">
                              Subtotal: {formatPrice(produto.precoTotal)}
                            </Badge>
                          </div>
                        )}
                      </div>
                    ))}

                    {reserva.valorTotal && (
                      <div className="mt-3 pt-3 border-top text-center">
                        <h4 className="text-primary mb-0">
                          Total da Reserva: {formatPrice(reserva.valorTotal)}
                        </h4>
                      </div>
                    )}
                  </Card.Body>
                </Card>
              )}

              {/* Status de Pagamento */}
              {(reserva.statusPagamento || (reserva.valorPago && reserva.valorPago > 0)) && (
                <Card className="mb-4 border-0 shadow-sm">
                  <Card.Header className="bg-warning text-dark">
                    <h6 className="mb-0">Informações de Pagamento</h6>
                  </Card.Header>
                  <Card.Body>
                    <Row>
                      {reserva.statusPagamento && (
                        <Col md={6}>
                          <p className="mb-2"><strong>Status:</strong> {getStatusPagamentoBadge(reserva.statusPagamento)}</p>
                        </Col>
                      )}
                      {reserva.valorPago && reserva.valorPago > 0 && (
                        <Col md={6}>
                          <p className="mb-0"><strong>Valor Pago:</strong> {formatPrice(reserva.valorPago)}</p>
                        </Col>
                      )}
                    </Row>
                  </Card.Body>
                </Card>
              )}

              {/* Observações */}
              {reserva.observacoes && (
                <Card className="border-0 shadow-sm">
                  <Card.Header className="bg-info text-white">
                    <h6 className="mb-0">Observações</h6>
                  </Card.Header>
                  <Card.Body>
                    <p className="mb-0">{reserva.observacoes}</p>
                  </Card.Body>
                </Card>
              )}
            </Card.Body>
          </Card>

          {/* Footer */}
          <div className="text-center text-muted">
            <p className="mb-0">
              <strong>SystemLoc</strong> - Sistema de Gestão para Lojas de Aluguel de Roupas
            </p>
            <small>Este link é único e seguro para visualizar sua reserva</small>
          </div>
        </Col>
      </Row>
    </Container>
  );
};

export default PublicReservaView;
